import React, { useState } from 'react';
import { Mail, Check, X, Plus, RefreshCw, Pencil } from 'lucide-react';

/**
 * EmailAccountIcon - Icon based on account type
 */
const EmailAccountIcon = ({ type, size = 12 }) => {
  switch (type) {
    case 'gmail':
      return (
        <svg className={`w-${size/4} h-${size/4}`} style={{ width: size, height: size }} viewBox="0 0 24 24">
          <path fill="currentColor" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
        </svg>
      );
    case 'icloud':
      return (
        <svg className={`w-${size/4} h-${size/4}`} style={{ width: size, height: size }} viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
      );
    default:
      return <Mail size={size} />;
  }
};

/**
 * EmailAccountTab - Individual email account tab with drag handle
 */
const EmailAccountTab = ({
  account,
  isActive,
  isRenaming,
  renameValue,
  setRenameValue,
  onSelect,
  onStartRename,
  onRename,
  onCancelRename,
  onRemove,
  onSync,
  onConnect,
  isLoading,
  isConnected,
  connectedEmail,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop
}) => {
  const getTabStyle = () => {
    if (!isActive) return 'bg-gray-800 text-gray-400 hover:bg-gray-700';
    switch (account.type) {
      case 'outlook': return 'bg-blue-600 text-white';
      case 'gmail': return 'bg-white text-black';
      case 'nylas': return 'bg-purple-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const getConnectBtnStyle = () => {
    if (isConnected || account.connected) {
      return 'bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/50';
    }
    switch (account.type) {
      case 'gmail': return 'bg-orange-600 hover:bg-orange-700 text-white';
      case 'outlook': return 'bg-blue-600 hover:bg-blue-700 text-white';
      default: return 'bg-blue-600 hover:bg-blue-700 text-white';
    }
  };

  if (isRenaming) {
    return (
      <div className="flex items-center gap-0.5 group">
        <div className="flex items-center gap-1 bg-gray-800 rounded-lg px-2 py-1">
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onRename();
              else if (e.key === 'Escape') onCancelRename();
            }}
            onBlur={() => renameValue.trim() ? onRename() : onCancelRename()}
            className="bg-transparent border-none outline-none text-white text-xs md:text-sm w-20 md:w-24"
            autoFocus
            data-testid={`rename-input-${account.id}`}
          />
          <button onClick={onRename} className="text-green-400 hover:text-green-300" title="Save">
            <Check size={12} />
          </button>
          <button onClick={onCancelRename} className="text-red-400 hover:text-red-300" title="Cancel">
            <X size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`flex items-center group/tab transition-all duration-200 ${isDragging ? 'opacity-50' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
    >
      {/* Account Tab Button - compact, expands on hover */}
      <button
        onClick={onSelect}
        onDoubleClick={() => onStartRename(account.name)}
        className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${getTabStyle()}`}
        title="Double-click to rename"
        data-testid={`email-account-${account.id}`}
      >
        <EmailAccountIcon type={account.type} size={12} />
        {account.name}
        <button
          onClick={(e) => { e.stopPropagation(); onStartRename(account.name); }}
          className="ml-1 opacity-0 group-hover/tab:opacity-100 transition-opacity hover:scale-110"
          title="Rename"
          data-testid={`rename-email-${account.id}`}
        >
          <Pencil size={10} />
        </button>
        {(isConnected || account.connected) && (
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" title="Connected"></span>
        )}
      </button>

      {/* Refresh button - appears on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSync();
        }}
        disabled={isLoading}
        className={`w-0 overflow-hidden opacity-0 group-hover/tab:w-5 group-hover/tab:opacity-100 group-hover/tab:ml-1 transition-all duration-200 p-0.5 rounded flex-shrink-0 ${
          isLoading 
            ? 'text-blue-400' 
            : 'text-gray-400 hover:text-blue-400'
        }`}
        title={isLoading ? 'Refreshing...' : `Refresh ${account.name || account.email}`}
        data-testid={`refresh-email-${account.id}`}
      >
        <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
      </button>

      {/* Remove button - appears on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="w-0 overflow-hidden opacity-0 group-hover/tab:w-5 group-hover/tab:opacity-100 group-hover/tab:ml-0.5 transition-all duration-200 p-0.5 rounded text-red-400 hover:text-red-300 flex-shrink-0"
        title={`Remove ${account.name || account.email}`}
        data-testid={`remove-email-${account.id}`}
      >
        <X size={12} />
      </button>
    </div>
  );
};

/**
 * EmailAccountTabs - Container for email account tabs with add button and drag-to-reorder
 */
const EmailAccountTabs = ({
  accounts = [],
  activeAccountId,
  renamingAccountId,
  renameValue,
  setRenameValue,
  onSelectAccount,
  onStartRename,
  onRename,
  onCancelRename,
  onRemoveAccount,
  onSyncAccount,
  onConnectAccount,
  onAddAccount,
  onReorderAccounts,
  // Account-specific states
  outlookConnected,
  outlookLoading,
  gmailConnected,
  gmailLoading,
  gmailEmail,
  nylasLoading = false,
  className = ''
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const getAccountState = (account) => {
    if (account.id === 'outlook') {
      return { isConnected: outlookConnected, isLoading: outlookLoading, email: null };
    }
    if (account.id === 'gmail') {
      return { isConnected: gmailConnected, isLoading: gmailLoading, email: gmailEmail };
    }
    // For Nylas accounts, use nylasLoading when this is the active account
    if (account.type === 'nylas') {
      return { isConnected: account.connected, isLoading: nylasLoading && activeAccountId === account.id, email: account.email };
    }
    return { isConnected: account.connected, isLoading: false, email: account.email };
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex && onReorderAccounts) {
      const newAccounts = [...accounts];
      const [draggedItem] = newAccounts.splice(draggedIndex, 1);
      newAccounts.splice(dropIndex, 0, draggedItem);
      onReorderAccounts(newAccounts);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className={`flex gap-0.5 overflow-x-auto items-center ${className}`}>
      {accounts.map((account, index) => {
        const { isConnected, isLoading, email } = getAccountState(account);
        return (
          <EmailAccountTab
            key={account.id}
            account={account}
            isActive={activeAccountId === account.id}
            isRenaming={renamingAccountId === account.id}
            renameValue={renameValue}
            setRenameValue={setRenameValue}
            onSelect={() => onSelectAccount(account.id)}
            onStartRename={(name) => onStartRename(account.id, name)}
            onRename={() => onRename(account.id, renameValue)}
            onCancelRename={onCancelRename}
            onRemove={() => onRemoveAccount(account.id)}
            onSync={() => onSyncAccount(account)}
            onConnect={() => onConnectAccount(account)}
            isLoading={isLoading}
            isConnected={isConnected}
            connectedEmail={email}
            isDragging={draggedIndex === index}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, index)}
          />
        );
      })}
      
      {/* Add Email Account Button */}
      <button
        onClick={onAddAccount}
        className="px-2 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1 bg-black text-gray-400 hover:bg-gray-900 hover:text-white border border-dashed border-gray-600"
        title="Connect new email account via Nylas"
        data-testid="add-email-btn"
      >
        <Plus size={12} />
        <span className="hidden sm:inline">Add</span>
      </button>
    </div>
  );
};

export default EmailAccountTabs;
